import { axiosClientv1 } from "./apiClient";
import { ApiResponse } from "../Types";

export const GetCustomerCredits = async (params: {
  page: number;
  limit: number;
  customer_id?: string | null;
}): Promise<ApiResponse<any>> => {
  try {
    const response = await axiosClientv1.get(`/credits/customer`, { params });
    return response.data;
  } catch (error: any) {
    return {
      success: false,
      message:
        error?.response?.data?.message || "Failed to load customer credits.",
      data: { credits: [], total: 0 },
      error: {
        code: error?.response?.status || 500,
        type: "server_error",
      },
    };
  }
};

export const GetAllCustomerCredits = async (params: {
  page: number;
  limit: number;
  customer_id?: string | null;
  sort_by?: string | null;
  sort_order?: string | null;
}): Promise<ApiResponse<any>> => {
  try {
    const response = await axiosClientv1.get(`/credits/all`, { params });
    return response.data;
  } catch (error: any) {
    return {
      success: false,
      message:
        error?.response?.data?.message || "Failed to load customer credits.",
      data: { credits: [], total: 0 },
      error: {
        code: error?.response?.status || 500,
        type: "server_error",
      },
    };
  }
};

export const CreateCustomerCredit = async (payload: {
  customer_id: string;
  order_id?: string | null;
  transaction_type_id: string;
  amount: number;
  payment_method_code?: string;
  bank_account_id?: string | null;
  transaction_no?: string | null;
  reference_no?: string | null;
  cheque_no?: string | null;
  bank_name?: string | null;
  cheque_date?: string | null;
  note?: string | null;
  transaction_date?: string | null;
}): Promise<ApiResponse<any>> => {
  try {
    const response = await axiosClientv1.post(
      `/credits/customer/create`,
      payload,
    );
    return response.data;
  } catch (error: any) {
    return {
      success: false,
      message:
        error?.response?.data?.message ||
        "Failed to record customer credit transaction.",
      error: {
        code: error?.response?.status || 400,
        type: "bad_request",
      },
    };
  }
};

export const GetCustomerCreditBalance = async (
  customerId: string,
): Promise<ApiResponse<any>> => {
  try {
    const response = await axiosClientv1.get(`/credits/customer/balance`, {
      params: { customer_id: customerId },
    });
    return response.data;
  } catch (error: any) {
    return {
      success: false,
      message:
        error?.response?.data?.message ||
        "Failed to load customer credit balance.",
      error: {
        code: error?.response?.status || 500,
        type: "server_error",
      },
    };
  }
};

export const GetCustomerOutstandingOrders = async (
  customerId: string,
): Promise<ApiResponse<any>> => {
  try {
    const response = await axiosClientv1.get(`/credits/customer/outstanding`, {
      params: { customer_id: customerId },
    });
    return response.data;
  } catch (error: any) {
    return {
      success: false,
      message:
        error?.response?.data?.message ||
        "Failed to load customer outstanding orders.",
      error: {
        code: error?.response?.status || 500,
        type: "server_error",
      },
    };
  }
};

export const GetSupplierCredits = async (params: {
  page: number;
  limit: number;
  supplier_id?: string | null;
}): Promise<ApiResponse<any>> => {
  try {
    const response = await axiosClientv1.get(`/credits/supplier`, { params });
    return response.data;
  } catch (error: any) {
    return {
      success: false,
      message:
        error?.response?.data?.message || "Failed to load supplier credits.",
      data: { data: [], total: 0 },
      error: {
        code: error?.response?.status || 500,
        type: "server_error",
      },
    };
  }
};

export const GetSupplierCreditBalance = async (
  supplierId: string,
): Promise<ApiResponse<any>> => {
  try {
    const response = await axiosClientv1.get(`/credits/supplier/balance`, {
      params: { supplier_id: supplierId },
    });
    return response.data;
  } catch (error: any) {
    return {
      success: false,
      message:
        error?.response?.data?.message ||
        "Failed to load supplier credit balance.",
      error: {
        code: error?.response?.status || 500,
        type: "server_error",
      },
    };
  }
};

export const CreateSupplierCredit = async (payload: {
  supplier_id: string;
  transaction_type_id: string;
  amount: number;
  payment_method_code?: string;
  bank_account_id?: string | null;
  note?: string | null;
  store_id?: string | null;
  transaction_date?: string | null;
}): Promise<ApiResponse<any>> => {
  try {
    const response = await axiosClientv1.post(
      `/credits/supplier/create`,
      payload,
    );
    return response.data;
  } catch (error: any) {
    return {
      success: false,
      message:
        error?.response?.data?.message ||
        "Failed to record supplier credit transaction.",
      error: {
        code: error?.response?.status || 400,
        type: "bad_request",
      },
    };
  }
};

export const UpdateCustomerCreditNote = async (payload: {
  id: string;
  note: string | null;
}): Promise<ApiResponse<any>> => {
  try {
    const response = await axiosClientv1.post(
      `/credits/customer/update-note`,
      payload,
    );
    return response.data;
  } catch (error: any) {
    return {
      success: false,
      message:
        error?.response?.data?.message || "Failed to update credit note.",
      error: {
        code: error?.response?.status || 400,
        type: "bad_request",
      },
    };
  }
};

export const GetSupplierOutstandingPurchases = async (params: {
  supplier_id: string;
}): Promise<ApiResponse<any>> => {
  try {
    const response = await axiosClientv1.get(
      `/credits/supplier/outstanding`,
      { params },
    );
    return response.data;
  } catch (error: any) {
    return {
      success: false,
      message:
        error?.response?.data?.message ||
        "Failed to load outstanding purchases.",
      error: {
        code: error?.response?.status || 500,
        type: "server_error",
      },
    };
  }
};

export const SupplierBulkPay = async (payload: {
  supplier_id: string;
  amount: number;
  payment_method_id: string;
  bank_account_id?: string | null;
  transaction_no?: string | null;
  reference_no?: string | null;
  cheque_no?: string | null;
  bank_name?: string | null;
  cheque_date?: string | null;
  transaction_date?: string | null;
  note?: string | null;
}): Promise<ApiResponse<any>> => {
  try {
    const response = await axiosClientv1.post(
      `/credits/supplier/bulk-pay`,
      payload,
    );
    return response.data;
  } catch (error: any) {
    return {
      success: false,
      message:
        error?.response?.data?.message || "Failed to process bulk payment.",
      error: {
        code: error?.response?.status || 400,
        type: "bad_request",
      },
    };
  }
};
